import { useState, useEffect } from 'react';
import { LoadScript } from '@react-google-maps/api';
import { GooglePlacesInput } from '@/components/ui/google-places-input';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogBody,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { cn } from '@/lib/utils';
import { Competitor, OTA_CHANNEL_COLORS } from '@/types/competitor';
import { OTAChannel } from '@/types/revenue-manager';

interface AddCompetitorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (competitor: Omit<Competitor, 'id' | 'createdAt' | 'updatedAt'>) => void;
  otaChannels: OTAChannel[];
  editingCompetitor?: Competitor | null;
  // New props for branch handling
  branches: Array<{ id: number; cm_property_id: string; name: string }>;
  currentPropertyId: string;
}

const DEFAULT_OTA_CHANNELS = [
  { id: 'booking', name: 'Booking.com', color: '#003580' },
  { id: 'makemytrip', name: 'Make My Trip', color: '#F7B500' },
  // { id: 'hotels', name: 'Hotels.com', color: '#D32F2F' },
  // { id: 'airbnb', name: 'Airbnb', color: '#FF5A5F' },
  // { id: 'agoda', name: 'Agoda', color: '#7B2EA0' },
  // { id: 'tripadvisor', name: 'TripAdvisor', color: '#34E0A1' },
];

export function AddCompetitorDialog({
  open,
  onOpenChange,
  onSave,
  otaChannels,
  editingCompetitor,
  branches = [],
  currentPropertyId,
}: AddCompetitorDialogProps) {
  const [name, setName] = useState('');
  const [location, setLocation] = useState('');
  const [distance, setDistance] = useState('');
  const [distanceUnit, setDistanceUnit] = useState<'km' | 'mi'>('km');
  const [starRating, setStarRating] = useState<string>('3');
  const [category, setCategory] = useState<'budget' | 'mid-range' | 'luxury'>('mid-range');
  const [marketPosition, setMarketPosition] = useState<'budget' | 'mid-range' | 'luxury'>('mid-range');
  const [priorityWeight, setPriorityWeight] = useState<number>(5);
  const [roomCount, setRoomCount] = useState('');
  const [amenities, setAmenities] = useState('');
  const [selectedOTAs, setSelectedOTAs] = useState<string[]>([]);
  const [isActive, setIsActive] = useState(true);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Combined OTA list
  // const allOTAs = otaChannels.length > 0
  //   ? otaChannels.map(c => ({ id: c.id, name: c.name, color: OTA_CHANNEL_COLORS[c.name.toLowerCase()] || '#666' }))
  //   : DEFAULT_OTA_CHANNELS;
 const allOTAs = DEFAULT_OTA_CHANNELS

  useEffect(() => {
    if (editingCompetitor) {
      setName(editingCompetitor.name);
      setLocation(editingCompetitor.location);
      setDistance(String(editingCompetitor.distance));
      setDistanceUnit(editingCompetitor.distanceUnit);
      setStarRating(String(editingCompetitor.starRating));
      setCategory(editingCompetitor.category);
      setMarketPosition(editingCompetitor.category || 'mid-range');
      setPriorityWeight(5);
      setRoomCount(String(editingCompetitor.rooms || 0));
      setAmenities(editingCompetitor.amenities.join(', '));
      setSelectedOTAs(editingCompetitor.trackedOTAs || []);
      setIsActive(editingCompetitor.is_active ?? true);
    } else {
      resetForm();
    }
  }, [editingCompetitor, open]);

  const resetForm = () => {
    setName('');
    setLocation('');
    setDistance('');
    setDistanceUnit('km');
    setStarRating('3');
    setCategory('mid-range');
    setMarketPosition('mid-range');
    setPriorityWeight(5);
    setRoomCount('');
    setAmenities('');
    setSelectedOTAs([]);
    setIsActive(true);
    setValidationErrors({});
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!name.trim()) {
      errors.name = 'Property name is required';
    }
    if (selectedOTAs.length === 0) {
      errors.otaChannels = 'Select at least one OTA channel';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    // Find the branch ID for the current property
    const currentBranch = branches.find((b) => b.cm_property_id === currentPropertyId);
    const branchId = currentBranch?.id;
    
    if (!branchId) {
      console.error('No valid branch found for property ID:', currentPropertyId);
      console.error('Available branches:', branches);
    }
    
    console.log('Saving competitor with branch ID:', branchId, 'for property ID:', currentPropertyId);

    const competitor: Omit<Competitor, 'id' | 'createdAt' | 'updatedAt'> = {
      name: name.trim(),
      location: location.trim() || undefined,
      distance: distance ? Number(distance) : undefined,
      distanceUnit: distance ? distanceUnit : undefined,
      starRating: starRating ? Number(starRating) : undefined,
      category,
      rooms: roomCount ? Number(roomCount) : undefined,
      amenities: amenities.split(',').map(a => a.trim()).filter(Boolean),
      trackedOTAs: selectedOTAs,
      is_active: isActive,
      branch_id: branchId, // Use the direct branch ID without fallback to 0
    };

    try {
      await onSave(competitor);
      onOpenChange(false);
      resetForm();
    } catch (error) {
      console.error('Error saving competitor:', error);
      // Keep modal open when there's an error
      // Show an error message or toast here if needed
    }
  };

  const toggleOTA = (otaId: string) => {
    setSelectedOTAs(prev =>
      prev.includes(otaId)
        ? prev.filter(id => id !== otaId)
        : [...prev, otaId]
    );
  };

  const selectAllOTAs = () => {
    setSelectedOTAs(allOTAs.map(o => o.id));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <LoadScript 
        googleMapsApiKey={import.meta.env.VITE_GOOGLE_API_KEY || ""} 
        libraries={["places"]} 
        loadingElement={<div className="p-4 text-center text-sm text-muted-foreground">Loading Google Maps...</div>}
        onError={(error) => {
          console.error('Google Maps API failed to load:', error);
        }}
        onLoad={() => {
          console.log('Google Maps API loaded successfully');
          // Check if the API key is valid by seeing if the places library is available
          if (!window.google?.maps?.places) {
            console.error('Google Places API not available. Check your API key configuration.');
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>{editingCompetitor ? 'Edit Competitor' : 'Add New Competitor'}</DialogTitle>
          <DialogDescription>
            Add a competitor property and select OTA channels to track
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-6">
          {/* Property Name & Location */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <GooglePlacesInput
                name="name"
                value={{ label: name, value: name }}
                labelName="Property Name"
                placeholder="Search hotels, businesses..."
                errorLabel={validationErrors.name}
                requiredStar={true}
                fieldName="name"
                isMulti={false}
                onChange={(e) => {
                  // Handle single location data - ensure we treat it as a single object (not array)
                  // Using type assertion since we know this is in single-select mode (isMulti=false)
                  const locationData = e.target.value as { label: string; value: string } | null;
                  if (locationData) {
                    console.log('Location data:', locationData);
                    
                    // Set the name from the first part before @ if it contains @
                    let nameValue = locationData.value;
                    if (nameValue.includes('@')) {
                      nameValue = nameValue.split('@')[0];
                    }
                    setName(nameValue);
                    
                    // If we have location data in the formatted address, use it
                    if (!location) {
                      // Try to extract a formatted address or use the name as fallback
                      const formattedLocation = locationData.label.includes(',') ? 
                        locationData.label.split(',').slice(1).join(',').trim() :
                        nameValue;
                      setLocation(formattedLocation);
                    }
                  } else {
                    // Reset if nothing selected
                    setName('');
                  }
                }}
                debug={true}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                placeholder="Downtown, City Center"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              />
            </div>
          </div>

          {/* Distance, Unit, Star Rating - Only shown when values are available */}
          <div className={`grid gap-4 ${distance ? "sm:grid-cols-2" : ""} ${distance && starRating ? "sm:grid-cols-3" : ""}`}>
            {distance ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="distance">Distance</Label>
                  <Input
                    id="distance"
                    type="number"
                    step="0.1"
                    placeholder="2.5"
                    value={distance}
                    onChange={(e) => setDistance(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Unit</Label>
                  <Select value={distanceUnit} onValueChange={(v: 'km' | 'mi') => setDistanceUnit(v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="km">Kilometers</SelectItem>
                      <SelectItem value="mi">Miles</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            ) : null}
            
            {starRating ? (
              <div className="space-y-2">
                <Label>Star Rating</Label>
                <Select value={starRating} onValueChange={setStarRating}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 Star</SelectItem>
                    <SelectItem value="2">2 Stars</SelectItem>
                    <SelectItem value="3">3 Stars</SelectItem>
                    <SelectItem value="4">4 Stars</SelectItem>
                    <SelectItem value="5">5 Stars</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : null}
          </div>

          {/* Category, Market Position, Room Count */}
          <div className={`grid gap-4 ${roomCount ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={category} onValueChange={(v: 'budget' | 'mid-range' | 'luxury') => setCategory(v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="budget">Budget</SelectItem>
                  <SelectItem value="mid-range">Mid-Range</SelectItem>
                  <SelectItem value="luxury">Luxury</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Market Position</Label>
              <Select value={marketPosition} onValueChange={(v: 'budget' | 'mid-range' | 'luxury') => setMarketPosition(v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="budget">Budget</SelectItem>
                  <SelectItem value="mid-range">Mid-Range</SelectItem>
                  <SelectItem value="luxury">Luxury</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {roomCount ? (
              <div className="space-y-2">
                <Label htmlFor="roomCount">Number of Rooms</Label>
                <Input
                  id="roomCount"
                  type="number"
                  placeholder="120"
                  value={roomCount}
                  onChange={(e) => setRoomCount(e.target.value)}
                />
              </div>
            ) : null}
          </div>

          {/* Priority Weight */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Priority Weight</Label>
              <span className="text-sm font-medium">{priorityWeight}/10</span>
            </div>
            <Slider
              value={[priorityWeight]}
              onValueChange={(value) => setPriorityWeight(value[0])}
              max={10}
              min={1}
              step={1}
            />
            <p className="text-xs text-muted-foreground">
              Higher weight means this competitor has more influence on price recommendations
            </p>
          </div>

          {/* Amenities */}
          <div className="space-y-2">
            <Label htmlFor="amenities">Amenities (comma-separated)</Label>
            <Input
              id="amenities"
              placeholder="Pool, Spa, Restaurant, Gym"
              value={amenities}
              onChange={(e) => setAmenities(e.target.value)}
            />
          </div>

          {/* OTA Channels */}
          <Card className={cn(validationErrors.otaChannels && 'border-destructive')}>
            <CardContent className="pt-4 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold">
                    OTA Channels to Track <span className="text-destructive">*</span>
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Select which booking platforms to monitor for this competitor
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={selectAllOTAs}>
                  Select All
                </Button>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                {allOTAs.map((ota) => (
                  <div
                    key={ota.id}
                    className={cn(
                      'flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors',
                      selectedOTAs.includes(ota.id)
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-muted-foreground/30'
                    )}
                    onClick={() => toggleOTA(ota.id)}
                  >
                    <Checkbox
                      checked={selectedOTAs.includes(ota.id)}
                      onCheckedChange={() => toggleOTA(ota.id)}
                    />
                    <span
                      className="h-2.5 w-2.5 rounded-full"
                      style={{ backgroundColor: ota.color }}
                    />
                    <span className="text-sm font-medium">{ota.name}</span>
                  </div>
                ))}
              </div>

              {validationErrors.otaChannels && (
                <p className="text-xs text-destructive">{validationErrors.otaChannels}</p>
              )}
            </CardContent>
          </Card>

          {/* Active Toggle */}
          <div className="flex items-center gap-3">
            <Switch
              checked={isActive}
              onCheckedChange={setIsActive}
            />
            <Label className="cursor-pointer" onClick={() => setIsActive(!isActive)}>
              Active (track pricing for this competitor)
            </Label>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            {editingCompetitor ? 'Update Competitor' : 'Add Competitor'}
          </Button>
        </DialogFooter>
      </LoadScript>
      </DialogContent>
    </Dialog>
  );
}
